import type { AuthorCandidateSource } from '~/utils/solr/author-utils'
import { SolrParamsKey } from '#shared/solr'
import { createAuthorCandidates } from '~/utils/solr/author-utils'
import { useAuthorQuery } from './useAuthorQuery'

export interface ResolvedAuthorLink {
  label: string
  slug?: string
  education?: string
}

interface AuthorMatch {
  slug?: string
  education?: string
}

function cleanEducation(value: string | undefined, label: string): string | undefined {
  const trimmed = value?.trim().replace(/[.,]\s*$/, '')
  return trimmed && trimmed !== label ? trimmed : undefined
}

export function useResolvedAuthorLinks(input: MaybeRefOrGetter<AuthorCandidateSource>): {
  links: ComputedRef<ResolvedAuthorLink[]>
} {
  const candidates = computed(() => createAuthorCandidates(toValue(input)))

  const ezObjectIds = computed(() => [...new Set(
    candidates.value.flatMap(c => c.ezObjectId ? [c.ezObjectId] : []),
  )])

  const fallbackSlugs = computed(() => [...new Set(
    candidates.value.flatMap(c => c.fallbackSlug ? [c.fallbackSlug] : []),
  )])

  const { data: byEzObjectId } = useAuthorQuery(
    computed(() => ({
      [SolrParamsKey.Authors]: { nodeType: 'author' as const, ezObjectIds: ezObjectIds.value },
    })),
    { enabled: computed(() => ezObjectIds.value.length > 0) },
  )

  const { data: byRemoteId } = useAuthorQuery(
    computed(() => ({
      [SolrParamsKey.Authors]: { nodeType: 'author' as const, remoteIds: fallbackSlugs.value },
    })),
    { enabled: computed(() => fallbackSlugs.value.length > 0) },
  )

  const ezObjectIdMatches = computed(() => {
    const map = new Map<string, AuthorMatch>()
    for (const doc of byEzObjectId.value?.response.docs ?? []) {
      const id = doc.ezObjectId !== undefined ? String(doc.ezObjectId) : undefined
      if (id) {
        map.set(id, { slug: doc.slug ?? undefined, education: doc.education ?? undefined })
      }
    }
    return map
  })

  const remoteIdMatches = computed(() => {
    const map = new Map<string, AuthorMatch>()
    for (const doc of byRemoteId.value?.response.docs ?? []) {
      if (doc.slug) {
        map.set(doc.slug, { slug: doc.slug, education: doc.education ?? undefined })
      }
    }
    return map
  })

  const links = computed<ResolvedAuthorLink[]>(() => {
    const ezMap = ezObjectIdMatches.value
    const slugMap = remoteIdMatches.value

    return candidates.value.map((c) => {
      const match = c.ezObjectId
        ? ezMap.get(c.ezObjectId)
        : c.fallbackSlug ? slugMap.get(c.fallbackSlug) : undefined

      return {
        label: c.label,
        slug: match?.slug,
        education: cleanEducation(match?.education, c.label),
      }
    })
  })

  return { links }
}
